import React from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { Modal } from 'react-bootstrap'
import { GhgLandingPageCalculationListItem } from './GhgLandingPage'
import { useAsync } from 'hooks/use-async'

interface Props {
  calc: GhgLandingPageCalculationListItem
  onClose: () => void
  onSubmit: (calc: GhgLandingPageCalculationListItem) => Promise<void | null>
}

export function DeleteCalculationModal(props: Props) {
  const { onSubmit, onClose } = props
  const [{ loading, error }, handleSubmit] = useAsync(onSubmit)
  return (
    <Modal
      show={true}
      animation={false}
      onHide={onClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      data-testid="delete-calculation-modal"
    >
      <DeleteCalculationModalView
        {...props}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
      />
    </Modal>
  )
}

interface ViewProps extends Props {
  loading: boolean
  error?: string
}

/**
 * We split the modal's content into a separate component, to make it easier
 * to work on it in Storybook. Also, portals don't work in Storyshots, so
 * we need the `<Modal>` component to be in the wrapper or else the Storyshot
 * will just be `null`.
 *
 * @param props
 */
export function DeleteCalculationModalView(props: ViewProps) {
  const { calc, onClose, onSubmit, loading, error } = props
  return (
    <>
      <Modal.Header closeButton id="contained-modal-title-vcenter">
        <Modal.Title>Delete calculation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          You are about to delete the calculation for {calc.name} you recorded on{' '}
          {calc.lastUpdatedFriendly}.
        </p>
        <p>Please confirm.</p>
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="sr-only">Deleting...</span>
            </Spinner>
          </div>
        ) : (
          error && (
            <h3 className="h6 font-weight-bold text-danger">
              Oops! Something went wrong while deleting the calculation.
            </h3>
          )
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" onClick={() => onSubmit(calc).catch(() => {})} disabled={loading}>
          Delete
        </Button>
      </Modal.Footer>
    </>
  )
}
